import React from "react";
import { Box, Grid } from "@mui/material";
import { EventNote as FuneralIcon, EventAvailable as ScheduledIcon, CheckCircleOutline as CompletedIcon } from "@mui/icons-material";
import {
  FuneralRegisterReport,
  PlannerPerformanceReport,
  ifasReportsService,
  IFASReportFilter,
} from "../../../../../services/ifasReportsService";
import { IFASReportTable, ColumnDef } from "../components/IFASReportTable";
import { ReportKpiCard, ReportStatusChip } from "../components/ReportPrimitives";

interface Props {
  funerals: FuneralRegisterReport[];
  planners: PlannerPerformanceReport[];
  loading: boolean;
  filter: IFASReportFilter;
}

export const FuneralManagementReportsTab: React.FC<Props> = ({
  funerals,
  planners,
  loading,
  filter,
}) => {
  const activeCount = funerals.filter((f) => f.status?.toLowerCase() === "active").length;
  const completedCount = funerals.filter((f) => f.status?.toLowerCase() === "completed").length;
  const scheduledCount = funerals.filter((f) => f.status?.toLowerCase() === "scheduled").length;

  const funeralColumns: ColumnDef<FuneralRegisterReport>[] = [
    { id: "funeralNumber", label: "Funeral No", minWidth: 130 },
    { id: "deceasedName", label: "Deceased", minWidth: 160 },
    { id: "deceasedIdNumber", label: "ID / Policy No", minWidth: 140 },
    { id: "branchName", label: "Branch", minWidth: 140 },
    { id: "opsCentreName", label: "Ops Centre", minWidth: 140 },
    { id: "funeralPlannerName", label: "Planner", minWidth: 150 },
    { id: "funeralType", label: "Type", minWidth: 130 },
    { id: "funeralDate", label: "Funeral Date", minWidth: 130 },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      format: (value) => <ReportStatusChip label={value} />,
    },
    { id: "cemeteryName", label: "Cemetery", minWidth: 150 },
    { id: "turnaroundDays", label: "Turnaround (Days)", minWidth: 140, align: "right" },
  ];

  const plannerColumns: ColumnDef<PlannerPerformanceReport>[] = [
    { id: "plannerName", label: "Funeral Planner", minWidth: 160 },
    { id: "branchName", label: "Branch", minWidth: 140 },
    { id: "opsCentreName", label: "Ops Centre", minWidth: 140 },
    { id: "totalAssigned", label: "Assigned", minWidth: 100, align: "right" },
    { id: "activeCount", label: "Active", minWidth: 100, align: "right" },
    { id: "completedCount", label: "Completed", minWidth: 100, align: "right" },
    { id: "cancelledCount", label: "Cancelled", minWidth: 100, align: "right" },
    { id: "avgTurnaroundDays", label: "Avg Turnaround", minWidth: 140, align: "right" },
    { id: "monthlyCount", label: "Monthly Count", minWidth: 120, align: "right" },
    { id: "ytdCount", label: "YTD Count", minWidth: 110, align: "right" },
  ];

  return (
    <Box>
      {/* Top Quick KPI Bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportKpiCard label="Total Funerals" value={funerals.length} caption="In selected period" tone="ink" icon={<FuneralIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportKpiCard label="Active & Scheduled" value={activeCount + scheduledCount} caption="Awaiting completion" tone="amber" icon={<ScheduledIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportKpiCard label="Completed" value={completedCount} caption={`${funerals.length ? Math.round((completedCount / funerals.length) * 100) : 0}% completion rate`} tone="green" icon={<CompletedIcon />} />
        </Grid>
      </Grid>

      <IFASReportTable
        title="Funeral Register"
        subtitle="All funerals for the selected period, branch, planner and status."
        columns={funeralColumns}
        data={funerals}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("funeral-register", fmt, filter)}
      />

      <IFASReportTable
        title="Planner Performance"
        subtitle="Assigned, completed and turnaround per planner."
        columns={plannerColumns}
        data={planners}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("planner-performance", fmt, filter)}
      />
    </Box>
  );
};
